import { supabase } from '../supabase';
import { collectAttributionData, ensureEventId } from '../attribution/attributionCookie';

/**
 * Resume della registrazione per sessioni "a metà".
 *
 * Con l'auto-login post-password (flusso email) e il signup social, un utente
 * che abbandona il wizard ha comunque una sessione valida ma ZERO ruoli: il
 * ruolo lo assegna solo register-complete. organization_id NON discrimina,
 * perché l'org placeholder nasce già a metà wizard (register-password /
 * register-oauth-start). Senza resume, quell'utente rimbalza in loop tra
 * '/' e '/login' (nessuna rotta accessibile → fallback '/login' → sessione
 * presente → '/').
 */
export async function isRegistrationPending(userId: string): Promise<boolean> {
  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('is_super_admin, is_partner_admin')
    .eq('id', userId)
    .maybeSingle();
  // Fail-open verso il comportamento storico: su errore non trattiamo
  // l'utente come "a metà" (mai mandare nel wizard un utente registrato).
  if (profileError) return false;
  // Super/partner admin non hanno righe user_roles: mai nel wizard.
  if (profile?.is_super_admin || profile?.is_partner_admin) return false;

  const { count, error } = await supabase
    .from('user_roles')
    .select('user_id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) return false;
  return (count ?? 0) === 0;
}

/**
 * Riapre il wizard per la sessione corrente riusando (lato edge) l'eventuale
 * registration_session e org placeholder esistenti. Ritorna l'URL dello step
 * su cui riprendere, o null se la edge rifiuta (es. utente già registrato) o
 * non è raggiungibile: in quel caso il chiamante decide il fallback.
 */
export async function getRegistrationResumeUrl(): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke('register-oauth-start', {
      body: { attributionData: ensureEventId(collectAttributionData()) }
    });
    if (error || !data?.sessionToken) return null;
    // resumeStep dalla registration_session: 3 = profilo+azienda da fare,
    // 4+ = azienda già compilata → dritto al completamento.
    const step = typeof data.resumeStep === 'number' ? data.resumeStep : 3;
    const page = step >= 4 ? 'complete' : 'profile';
    return `/register/${page}?token=${data.sessionToken}`;
  } catch {
    return null;
  }
}
